<template>
  <div>
    <sale-prices-rules-filter
      ref="salePricesFilter"
      :values="filterValues"
      :filter-submitting="isLoading"
      @filter="setFilter"
    />

    <vue-good-table
      mode="remote"
      :sort-options="{ enabled: false }"
      :total-rows="totalRecords"
      :rows="rows"
      :columns="getColumns"
      :pagination-options="{
        enabled: true,
        jumpFirstOrLast: true,
        rowsPerPageLabel: t('Rows per page'),
        ofLabel: t('ofLabel'),
        nextLabel: t('nextLabel'),
        prevLabel: t('prevLabel'),
        dropdownAllowAll: false
      }"
      @on-page-change="onPageChange"
      @on-per-page-change="onPerPageChange"
    >
      <table-no-result-found slot="emptystate" :loading="isLoading" />
      <template slot="table-row" slot-scope="props">
        <span v-if="props.column.field === 'id'">
          {{ props.row.id ? props.row.id : '-' }}
        </span>
        <span v-else-if="props.column.field === 'provider'">
          {{ props.row.providerName || '-' }}
        </span>
        <span v-else-if="props.column.field === 'customer'">
          {{ props.row.customerName || '-' }}
        </span>
        <span v-else-if="props.column.field === 'type'">
          {{ props.row.type ? props.row.type : '-' }}
        </span>
        <span v-else-if="props.column.field === 'operator'">
          {{ props.row.operator ? props.row.operator : '-' }}
        </span>
        <span v-else-if="props.column.field === 'countries'">
          <template v-if="props.row.countries && props.row.countries.length">
            <span v-if="props.row.countries.length <= 2">
              <b-badge
                v-for="country in props.row.countries"
                :key="country.id || country.iso2"
                variant="light-primary"
                class="mr-50 mb-50"
              >
                {{ getCountryLabel(country) }}
              </b-badge>
            </span>
            <span v-else>
              <b-badge
                v-for="country in props.row.countries.slice(0, 2)"
                :key="country.id || country.iso2"
                variant="light-primary"
                class="mr-50 mb-50"
              >
                {{ getCountryLabel(country) }}
              </b-badge>
              <b-badge
                :id="`sale-country-tooltip-${props.row.id}`"
                variant="light-secondary"
                class="cursor-pointer"
              >
                +{{ props.row.countries.length - 2 }}
              </b-badge>
              <b-tooltip
                :target="`sale-country-tooltip-${props.row.id}`"
                triggers="hover"
                placement="top"
              >
                {{ props.row.countries.map(c => getCountryLabel(c)).join(', ') }}
              </b-tooltip>
            </span>
          </template>
          <span v-else>-</span>
        </span>
        <span v-else-if="props.column.field === 'value'">
          {{ Number(props.row.rawType) === 6 ? '-' : props.row.value ? props.row.value : '-' }}
        </span>
        <span v-else-if="props.column.field === 'pricingType'">
          {{ props.row.pricingType ? props.row.pricingType : '-' }}
        </span>
        <span v-else-if="props.column.field === 'price'">
          {{ props.row.price ? props.row.price : '-' }}
        </span>
        <span v-else-if="props.column.field === 'minimumPrice'">
          {{
            props.row.minimumPrice != null && props.row.minimumPrice !== ''
              ? props.row.minimumPrice
              : '-'
          }}
        </span>
        <span v-else-if="props.column.field === 'salePrice'">
          {{ props.row.salePrice ? props.row.salePrice : '-' }}
        </span>
        <span v-else-if="props.column.field === 'saleMinimumPrice'">
          {{
            props.row.saleMinimumPrice != null && props.row.saleMinimumPrice !== ''
              ? props.row.saleMinimumPrice
              : '-'
          }}
        </span>
        <span v-else-if="props.column.field === 'actions'">
          <span class="d-flex justify-content-center table-icon-buttons">
            <router-link
              :to="{
                name: 'sale-prices-rules-form',
                params: {
                  providerId: props.row.providerId,
                  customerId: props.row.customerId
                }
              }"
            >
              <b-button
                :id="`show-sale-price-button-${props.row.id}`"
                variant="primary"
                type="button"
                size="sm"
              >
                <feather-icon icon="EyeIcon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`show-sale-price-button-${props.row.id}`"
                  :title="t('Detail')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>
            </router-link>
          </span>
        </span>
      </template>
    </vue-good-table>
  </div>
</template>

<script>
import { BTooltip, BButton, BBadge } from 'bootstrap-vue';
import { VueGoodTable } from 'vue-good-table';
import { helper } from '@core/mixins/helper.js';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import useService from '@/auth/service/useService';
import SalePricesRulesFilter from './SalePricesRulesFilter.vue';

export default {
  components: {
    VueGoodTable,
    BTooltip,
    BButton,
    BBadge,
    SalePricesRulesFilter
  },
  mixins: [helper],
  data() {
    return {
      isLoading: true,
      totalRecords: 0,
      rows: [],
      filter: {
        page: 1,
        limit: 10
      },
      filterValues: {
        providerId: ''
      },
      typeOptions: [],
      operatorOptions: [],
      pricingTypeOptions: []
    };
  },
  computed: {
    getColumns() {
      return [
        {
          label: this.$t('ID'),
          field: 'id'
        },
        {
          label: this.$t('Provider'),
          field: 'provider'
        },
        {
          label: this.$t('Customer'),
          field: 'customer'
        },
        {
          label: this.$t('Type'),
          field: 'type'
        },
        {
          label: this.$t('Operator'),
          field: 'operator'
        },
        {
          label: this.$t('Countries'),
          field: 'countries'
        },
        {
          label: this.$t('Value'),
          field: 'value'
        },
        {
          label: this.$t('Pricing Type'),
          field: 'pricingType'
        },
        {
          label: this.$t('Cost Price'),
          field: 'price'
        },
        {
          label: this.$t('Cost Min Price'),
          field: 'minimumPrice'
        },
        {
          label: this.$t('Sale Price'),
          field: 'salePrice'
        },
        {
          label: this.$t('Sale Min Price'),
          field: 'saleMinimumPrice'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        }
      ];
    }
  },
  created() {
    this.$store.dispatch('app/getCountries');
    this.loadItems();
  },
  methods: {
    getCountryLabel(country) {
      if (!country) return '';
      const isTr = this.$i18n.locale === 'tr';
      if (typeof country === 'object') {
        const name = (isTr && country.nameTr ? country.nameTr : country.name) || country.iso2;
        if (name) return name;
      }
      const countryId = typeof country === 'object' ? country.id : country;
      const storeCountries = this.$store?.getters['app/countries'] || [];
      const found = storeCountries.find(c => Number(c.value || c.id) === Number(countryId));
      if (found) {
        return (
          (isTr && found.nameTr ? found.nameTr : found.text || found.name) ||
          found.iso2 ||
          countryId
        );
      }
      return countryId;
    },
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },
    async loadItems() {
      this.isLoading = true;

      try {
        const requestData = {
          ...this.filter,
          ...this.getFilteredObject(this.filterValues)
        };

        if (
          !this.typeOptions.length ||
          !this.operatorOptions.length ||
          !this.pricingTypeOptions.length
        ) {
          const [typeOptionsResponse, operatorOptionsResponse, pricingTypeOptionsResponse] =
            await Promise.all([
              useService.constantService.getProviderRuleType(),
              useService.constantService.getProviderRuleOperator(),
              useService.constantService.getProviderRulePricingType()
            ]);

          this.typeOptions = typeOptionsResponse.data;
          this.operatorOptions = operatorOptionsResponse.data;
          this.pricingTypeOptions = pricingTypeOptionsResponse.data;
        }

        const response = await useService.pricingService.getAllSalePricesRules(requestData);
        this.rows = response.data.map(item => ({
          ...item,
          rawType: item.type,
          type: this.typeOptions.find(type => type.value === item.type)?.info || item.type,
          operator:
            this.operatorOptions.find(operator => operator.value === item.operator)?.info ||
            item.operator,
          pricingType:
            this.pricingTypeOptions.find(pricingType => pricingType.value === item.pricingType)
              ?.info || item.pricingType
        }));
        this.totalRecords = response.meta.total;
      } catch (error) {
        this.showErrorMessage(error);
      } finally {
        this.isLoading = false;
      }
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },
    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    setFilter(data) {
      this.filterValues = data
        ? { ...this.defaultFilterValues, ...data }
        : { ...this.defaultFilterValues };
      this.filter.page = 1;
      this.loadItems();
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
